import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import SignupModal from '../components/SignupModal';

import './LoginPage.css';

const LoginPage: React.FC = () => {
  const { login, isLoading, error } = useAuth();
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [pendingUserData, setPendingUserData] = useState<any>(null);

  useEffect(() => {
    // Google OAuth 스크립트 로드
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.google && window.google.accounts) {
        try {
          window.google.accounts.id.initialize({
            client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '836425439040-ann0lm2g5idq74t7jplqe1ndh19a1ftb.apps.googleusercontent.com',
            callback: handleGoogleLogin,
            cancel_on_tap_outside: false,
            auto_select: false,
            itp_support: true,
            // 로컬 개발에서는 popup 모드 사용
            ux_mode: import.meta.env.VITE_NODE_ENV === 'development' ? 'popup' : 'redirect',
            ...(import.meta.env.VITE_NODE_ENV !== 'development' && {
              login_uri: window.location.origin + '/auth/callback'
            })
          });

          // 버튼 렌더링
          const buttonElement = document.getElementById('google-signin-button');
          if (buttonElement) {
            window.google.accounts.id.renderButton(buttonElement, {
              theme: 'outline',
              size: 'large',
              width: 300,
              text: 'signin_with',
              shape: 'rectangular',
            });
          }

        } catch (error) {
          console.error('Google OAuth initialization failed:', error);
        }
      }
    };

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleGoogleLogin = async (response: any) => {
    try {
      if (!response || !response.credential) {
        console.error('Invalid Google response:', response);
        throw new Error('유효하지 않은 Google 인증 응답');
      }

      console.log('Google login response received:', { hasCredential: !!response.credential });
      const result = await login(response.credential);

      if (result === null) {
        // 로그인 성공 - 자동으로 리다이렉트됨
        console.log('Login successful, redirecting...');
        window.location.href = '/dashboard';
      }
    } catch (err) {
      console.error('Google login failed:', err);
      // 사용자에게 에러 메시지 표시
    }
  };

  const handleSignupConfirm = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userData: pendingUserData })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          localStorage.setItem('userId', result.data.user_id);
          localStorage.setItem('authToken', result.data.token);
          window.location.reload(); // 페이지 새로고침
        }
      }
    } catch (err) {
      console.error('Signup failed:', err);
    } finally {
      setShowSignupModal(false);
      setPendingUserData(null);
    }
  };

  const handleSignupCancel = () => {
    setShowSignupModal(false);
    setPendingUserData(null);
  };



  return (
    <>
      <div className="login-v2-page">
        <div className="login-v2-background" aria-hidden="true">
          <div className="login-v2-orb login-v2-orb-left"></div>
          <div className="login-v2-orb login-v2-orb-right"></div>
        </div>

        <div className="login-v2-shell">
          <section className="login-v2-brand">
            <div className="login-v2-badge">AWS Certification Lab</div>
            <h1>AWS Exam Practice</h1>
            <p className="login-v2-subtitle">
              실전과 유사한 환경에서 AWS 자격증 시험을 반복 학습하세요.
            </p>

            <div className="login-v2-feature-list">
              <article className="login-v2-feature">
                <span className="login-v2-feature-dot">01</span>
                <div>
                  <h3>실전형 모의시험</h3>
                  <p>시험 시간, 문제 수, 흐름을 실제와 유사하게 제공합니다.</p>
                </div>
              </article>
              <article className="login-v2-feature">
                <span className="login-v2-feature-dot">02</span>
                <div>
                  <h3>문항별 분석 리포트</h3>
                  <p>오답 패턴과 약점 도메인을 빠르게 파악할 수 있습니다.</p>
                </div>
              </article>
              <article className="login-v2-feature">
                <span className="login-v2-feature-dot">03</span>
                <div>
                  <h3>진행 상태 자동 저장</h3>
                  <p>중단 후 이어풀기와 과거 결과 비교를 지원합니다.</p>
                </div>
              </article>
            </div>
          </section>

          <section className="login-v2-card-wrapper">
            <div className="login-v2-card">
              <h2>로그인</h2>
              <p>[변경 확인용] Google SSO로 로그인 후 모의고사를 시작하세요.</p>

              {error && (
                <div className="login-v2-error">
                  {error}
                </div>
              )}

              <div className="login-v2-google-area">
                <div id="google-signin-button" className="login-v2-google-button"></div>
                {isLoading && <span className="login-v2-loading">로그인 처리 중...</span>}
              </div>
            </div>
          </section>
        </div>
      </div>

      <SignupModal
        isOpen={showSignupModal}
        userData={pendingUserData || { email: '', name: '', google_id: '' }}
        onConfirm={handleSignupConfirm}
        onCancel={handleSignupCancel}
      />
    </>
  );
};

// Google OAuth 타입 정의
declare global {
  interface Window {
    google: any;
  }
}

export default LoginPage;
